(() => {
  if (customElements.get('swe-home-orthoride')) return;
  customElements.define('swe-home-orthoride', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.stage = this.querySelector('.swe-ortho__stage');
      this.photo = this.querySelector('.swe-ortho__photo');
      this.svg = this.querySelector('.swe-ortho__pointers');
      this.buttons = [...this.querySelectorAll('[data-callout]')];
      this.details = [...this.querySelectorAll('[data-detail]')];
      this.pointers = [...this.querySelectorAll('[data-pointer]')];
      this.replay = this.querySelector('.swe-ortho__replay');
      this.status = this.querySelector('[role="status"]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 989px)');
      this.canAnimate = CSS.supports('mask-image', 'linear-gradient(black, transparent)') || CSS.supports('-webkit-mask-image', 'linear-gradient(black, transparent)');
      this.classList.add('is-enhanced');
      this.buttons.forEach(button => {
        button.disabled = false;
        button.addEventListener('click', () => { this.finish(); this.select(button.dataset.callout, true); }, { signal });
        for (const event of ['pointerenter', 'pointerleave']) button.addEventListener(event, () => {
          this.pointers.find(pointer => pointer.dataset.pointer === button.dataset.callout)?.classList.toggle('is-hovered', event === 'pointerenter');
        }, { signal });
      });
      this.select(this.buttons[0]?.dataset.callout);
      this.addEventListener('focusin', () => { if (this.phase !== 'complete') this.finish(); }, { signal });
      this.addEventListener('pointerdown', event => {
        if (!this.replay.contains(event.target) && this.phase !== 'complete') this.finish();
      }, { signal });
      this.replay.addEventListener('click', () => this.play(), { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.finish();
        else this.replay.hidden = !this.imagesReady || !this.canAnimate;
      }, { signal });
      this.mobile.addEventListener('change', () => this.positionPointers(), { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden && this.frame) this.finish(); }, { signal });
      this.addEventListener('shopify:block:select', event => {
        this.finish(); this.select(event.detail.blockId, true);
      }, { signal });
      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(() => this.positionPointers());
        this.resize.observe(this.stage);
      } else window.addEventListener('resize', () => this.positionPointers(), { signal });
      document.fonts?.ready.then(() => { if (!signal.aborted) this.positionPointers(); });
      this.positionPointers();

      if (this.motion.matches || !this.canAnimate || this.dataset.autoplay !== 'true' || this.played) this.finish();
      else {
        this.phase = 'waiting'; this.classList.add('is-intro'); this.draw(0);
        if (!('IntersectionObserver' in window)) this.finish();
        else {
          this.observer = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.45)) return;
            this.inView = true;
            if (this.imagesReady) this.play();
            else if (!this.decodeTimer) this.decodeTimer = setTimeout(() => {
              if (!signal.aborted) this.imageFallback();
            }, 8000);
          }, { threshold: 0.45 });
          this.observer.observe(this.photo);
        }
      }
      // Decode only the two aligned pad layers. A missing logo must not disable the product.
      Promise.all([...this.querySelectorAll('[data-pad-image]')].map(img => img.decode())).then(() => {
        if (signal.aborted) return;
        clearTimeout(this.decodeTimer);
        this.imagesReady = true;
        this.failed = false;
        this.replay.hidden = !this.canAnimate || this.motion.matches;
        this.positionPointers();
        if (this.phase !== 'waiting') { this.finish(); return; }
        if (this.inView) this.play();
      }).catch(() => { if (!signal.aborted) this.imageFallback(); });
    }
    select(id, announce = false) {
      if (!this.buttons.some(button => button.dataset.callout === id)) return;
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.callout === id)));
      this.details.forEach(detail => {
        const selected = detail.dataset.detail === id;
        detail.classList.toggle('is-active', selected);
        detail.setAttribute('aria-hidden', String(!selected));
      });
      this.pointers.forEach(pointer => pointer.classList.toggle('is-active', pointer.dataset.pointer === id));
      if (announce) this.status.textContent = this.details.find(detail => detail.dataset.detail === id).textContent.trim();
    }
    positionPointers() {
      const stage = this.stage.getBoundingClientRect();
      const photo = this.photo.getBoundingClientRect();
      if (!stage.width || !stage.height) return;
      this.svg.setAttribute('viewBox', `0 0 ${stage.width} ${stage.height}`);
      this.pointers.forEach(pointer => {
        const button = this.buttons.find(item => item.dataset.callout === pointer.dataset.pointer);
        if (!button) return;
        const rect = button.getBoundingClientRect();
        const x = photo.left - stage.left + photo.width * Number(pointer.dataset.x) / 100;
        const y = photo.top - stage.top + photo.height * Number(pointer.dataset.y) / 100;
        const left = rect.left < photo.left;
        const startX = this.mobile.matches ? Math.min(x + 26, stage.width - 12) : (left ? rect.right : rect.left) - stage.left;
        const startY = this.mobile.matches ? Math.max(y - 22, 8) : rect.top - stage.top + rect.height / 2;
        const elbow = this.mobile.matches ? startX : startX + (x - startX) * 0.4;
        pointer.querySelector('path').setAttribute('d', `M ${startX} ${startY} H ${elbow} L ${x} ${y}`);
        pointer.querySelectorAll('circle').forEach(circle => { circle.setAttribute('cx', x); circle.setAttribute('cy', y); });
      });
    }
    draw(value) {
      this.value = value;
      const edge = 8 + value * 0.36;
      this.photo.style.setProperty('--reveal-solid', `${edge - 1.5}%`);
      this.photo.style.setProperty('--reveal-clear', `${edge + 1.5}%`);
    }
    play() {
      if (!this.imagesReady || this.motion.matches || !this.canAnimate) { this.finish(); return; }
      this.stop(); this.observer?.disconnect(); this.played = true; this.phase = 'reveal';
      this.classList.remove('is-details-visible', 'is-cta-visible', 'is-celebrating');
      this.classList.add('is-intro');
      [...this.buttons, ...this.pointers].forEach(item => item.classList.remove('is-presented'));
      this.draw(0);
      this.status.textContent = 'Revealing the pad construction.';
      const start = performance.now();
      const tick = now => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / 2000, 1);
        this.draw(100 * progress * progress * (3 - 2 * progress));
        if (elapsed >= 2000) {
          this.phase = 'callouts';
          this.buttons.forEach((button, i) => {
            if (elapsed >= 2000 + i * 180) {
              button.classList.add('is-presented'); this.pointers[i]?.classList.add('is-presented');
            }
          });
          this.classList.add('is-details-visible');
        }
        if (elapsed >= 3000) { this.phase = 'cta'; this.classList.add('is-cta-visible', 'is-celebrating'); }
        if (elapsed >= 3500) { this.finish(); return; }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    finish() {
      this.stop(); clearTimeout(this.decodeTimer); this.observer?.disconnect(); this.played = true; this.phase = 'complete';
      this.draw(this.failed ? 0 : 100);
      this.classList.remove('is-intro', 'is-celebrating');
      this.classList.add('is-details-visible', 'is-cta-visible');
      [...this.buttons, ...this.pointers].forEach(item => item.classList.add('is-presented'));
      this.replay.hidden = !this.imagesReady || !this.canAnimate || this.motion.matches;
      this.status.textContent = '';
    }
    imageFallback() {
      clearTimeout(this.decodeTimer);
      this.failed = true; this.imagesReady = false; this.finish();
      this.querySelector('.swe-ortho__base').alt = 'OrthoRide Correction Saddle Pad, natural felt with brown wear leathers.';
    }
    stop() { cancelAnimationFrame(this.frame); this.frame = 0; }
    disconnectedCallback() {
      this.stop(); clearTimeout(this.decodeTimer); this.observer?.disconnect(); this.resize?.disconnect();
      this.controller?.abort(); this.controller = null;
    }
  });
})();
